import { Entity } from '@backstage/catalog-model';

/**
 * The repo an agent works on. Not a key of this plugin's own: every entity the
 * GitHub discovery provider ingests already carries it, and it is also the clone
 * URL and the scope of the installation token, so a second key would only be a
 * second thing to keep in step.
 */
export const SANDBOXED_REPO_SLUG_ANNOTATION = 'github.com/project-slug';

/** Preset offered first in the launch dialog. */
export const SANDBOXED_PRESET_ANNOTATION = 'sandboxd.io/preset';

/** Comma-separated host tags every sandbox on this entity requires. */
export const SANDBOXED_TAGS_ANNOTATION = 'sandboxd.io/tags';

/** Branch to clone; the repo's default when unset. */
export const SANDBOXED_BASE_BRANCH_ANNOTATION = 'sandboxd.io/base-branch';

/**
 * Port the repo's dev server listens on, offered as the preview of any sandbox
 * whose preset declares none — so nobody has to remember it.
 */
export const SANDBOXED_PREVIEW_PORT_ANNOTATION = 'sandboxd.io/preview-port';

export interface SandboxedEntityRefs {
  /** `owner/repo`, as written on the entity. */
  slug: string;
  owner: string;
  repo: string;
  preset?: string;
  tags: string[];
  baseBranch?: string;
  previewPort?: number;
}

function parsePort(raw: string | undefined): number | undefined {
  const port = Number(raw?.trim() || NaN);
  return Number.isInteger(port) && port >= 1 && port <= 65535
    ? port
    : undefined;
}

/** A slug is `owner/repo` and nothing else — a trailing path is a different repo. */
function parseSlug(raw: string): { owner: string; repo: string } | undefined {
  const parts = raw.split('/');
  if (parts.length !== 2) return undefined;
  const [owner, repo] = parts.map(p => p.trim());
  if (!owner || !repo) return undefined;
  return { owner, repo };
}

export function readSandboxedAnnotations(
  entity: Entity,
): SandboxedEntityRefs | undefined {
  const annotations = entity.metadata.annotations ?? {};
  const slug = annotations[SANDBOXED_REPO_SLUG_ANNOTATION]?.trim();
  if (!slug) return undefined;

  const parsed = parseSlug(slug);
  if (!parsed) return undefined;

  return {
    slug: `${parsed.owner}/${parsed.repo}`,
    ...parsed,
    preset: annotations[SANDBOXED_PRESET_ANNOTATION]?.trim() || undefined,
    tags: (annotations[SANDBOXED_TAGS_ANNOTATION] ?? '')
      .split(',')
      .map(t => t.trim())
      .filter(Boolean),
    baseBranch:
      annotations[SANDBOXED_BASE_BRANCH_ANNOTATION]?.trim() || undefined,
    previewPort: parsePort(annotations[SANDBOXED_PREVIEW_PORT_ANNOTATION]),
  };
}

/** True when the entity carries enough to render the tab. */
export function isSandboxedAvailable(entity: Entity): boolean {
  return readSandboxedAnnotations(entity) !== undefined;
}
